import { Box, Button, CircularProgress, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { parsePhoneNumber } from "awesome-phonenumber";
import ky from "ky";
import "altcha";

import { capitalizeName } from "../utils/utils";

import FormTextField from "./FormTextField";
import StandardAlert from "./StandardAlert";
import Altcha from "./Altcha";

const defaultValues = {
  name: "",
  email: "",
  phone: "",
};

export default function Form() {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(defaultValues);
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertLevel, setAlertLevel] = useState("error");
  const [phoneError, setPhoneError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const altchaRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "name":
        setFormValues({
          ...formValues,
          [name]: capitalizeName(value),
        });
        break;

      case "phone":
        const pn = parsePhoneNumber(value, { regionCode: "US" });
        setPhoneError(!pn.valid);
        setFormValues({
          ...formValues,
          [name]: pn.valid ? pn.number.national : value,
        });
        break;

      case "email":
        const isValidEmail = validateEmail(value);
        setFormValues({
          ...formValues,
          [name]: value,
        });
        setEmailError(!isValidEmail);
        break;

      default:
        setFormValues({
          ...formValues,
          [name]: value,
        });
        break;
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const altcha = altchaRef.current?.value;
    console.log(altcha);

    if (
      formValues.name !== "" &&
      formValues.email !== "" &&
      formValues.phone !== "" &&
      !phoneError &&
      !emailError &&
      altcha
    ) {
      const verified = await ky
        .post("https://altcha.byerline.me/verify", {
          json: { altcha, serviceName: "adventure-playhouses" },
        })
        .json()
        .catch((err) => {
          console.error("AltCha verification failed:", err);
          return { verified: false };
        });

      if (!verified.verified) {
        setAlertLevel("error");
        setAlertMessage("CAPTCHA failed. Please try again.");
        setAlertIsOpen(true);
        return;
      }

      const slackMessage = {
        text: `New Inquiry from Adventure Playhouses!\n*Name:* ${formValues.name}\n*Email:* ${formValues.email}\n*Phone:* ${formValues.phone}`,
      };

      setIsLoading(true);

      ky.post(window._env_.REACT_APP_SLACK_WEBHOOK_URL, {
        json: slackMessage,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })
        .then(() => {
          setAlertLevel("success");
          setAlertMessage("Inquiry sent successfully!");
          setAlertIsOpen(true);
        })
        .catch((error) => {
          console.error("Failed to send message to Slack", error);
          setAlertLevel("error");
          setAlertMessage("Failed to send your message. Please try again.");
          setAlertIsOpen(true);
        })
        .finally(() => {
          setIsLoading(false);
          setFormValues(defaultValues);
        });
    } else if (phoneError) {
      setAlertLevel("error");
      setAlertMessage("Invalid Phone Number!");
      setAlertIsOpen(true);
    } else if (emailError) {
      setAlertLevel("error");
      setAlertMessage("Invalid E-Mail Address!");
      setAlertIsOpen(true);
    } else {
      setAlertLevel("error");
      setAlertMessage("Please make sure all required fields are populated!");
      setAlertIsOpen(true);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        ml: { xs: 0, md: -15 },
        mt: { xs: 0, sm: -18, md: 0 },
      }}
    >
      <FormTextField
        label="name"
        value={formValues.name}
        name="name"
        onChange={handleInputChange}
      />
      <FormTextField
        label="phone number"
        value={formValues.phone}
        name="phone"
        onChange={handleInputChange}
      />
      <FormTextField
        label="email"
        value={formValues.email}
        name="email"
        onChange={handleInputChange}
      />
      <Altcha ref={altchaRef} />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={isLoading}
        sx={{
          bgcolor: theme.palette.secondary.main,
          textTransform: "none",
          fontWeight: "bold",
          color: "white",
          "&:hover": {
            bgcolor: theme.palette.secondary.dark,
          },
        }}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "start your adVenture"
        )}
      </Button>

      <StandardAlert
        alertIsOpen={alertIsOpen}
        setAlertIsOpen={setAlertIsOpen}
        alertLevel={alertLevel}
        alertMessage={alertMessage}
        width="100%"
      />
    </Box>
  );
}
